import { showToast } from '@lokalise/louis';
import { useCallback, useEffect } from 'react';

import { type Language, useDeleteStyleGuideMutation } from '@app/api/styleGuide';
import noop from '@app/utils/noop';

import { useStyleGuideContext } from '../../StyleGuideContext';
import {
    type FileUpload,
    type FileUploadStaged,
    type FileUploadSuccess,
    fileUploadToStyleGuideEdit,
    isFileUploadStaged,
    matchFileUpload,
} from '../../model/fileUpload';
import { type RejectedFile } from '../../model/rejectedFile';
import useFileUpload, { UploadAbortedError } from '../useFileUpload';
import useStyleGuideUploadStatus from '../useStyleGuideUploadStatus';

const useUploadStyleGuide = () => {
    const { teamId, filesToUpload, filesRejectedForUpload, acknowledgeStyleGuideFiles, saveStyleGuides } =
        useStyleGuideContext();
    const [deleteStyleGuide] = useDeleteStyleGuideMutation();
    const { uploadFile, cancelUpload } = useFileUpload(teamId);
    const [
        uploads,
        {
            addFiles,
            hasBeenAdded,
            setLoadingProgress,
            setUploadSuccess,
            remove,
            setRemoving,
            setUploadError,
            reset,
            stageUpload,
            updateStaged,
        },
    ] = useStyleGuideUploadStatus();

    const cancelAndRemove = useCallback(
        async (fileUpload: FileUpload) => {
            cancelUpload(fileUpload.file);
            remove(fileUpload);
        },
        [cancelUpload, remove],
    );

    const deleteAndRemove = useCallback(
        async (fileUpload: FileUploadStaged | FileUploadSuccess) => {
            setRemoving(fileUpload);

            const response = await deleteStyleGuide({ teamId, styleGuideId: fileUpload.styleGuideId });

            if ('error' in response) {
                setUploadError(fileUpload.file, 'Delete failed', () => deleteAndRemove(fileUpload));

                return;
            }

            remove(fileUpload);
        },
        [deleteStyleGuide, remove, setRemoving, setUploadError, teamId],
    );

    const onRemoveCallback = useCallback(
        (fileUpload: FileUpload) =>
            matchFileUpload(fileUpload, {
                pendingCase: async (upload) => remove(upload),
                errorCase: async (upload) => remove(upload),
                uploadingCase: cancelAndRemove,
                stagedCase: deleteAndRemove,
                successCase: deleteAndRemove,
                removedCase: noop,
                removingCase: noop,
            }),
        [cancelAndRemove, deleteAndRemove, remove],
    );

    const addFilesCallback = useCallback(
        (files: File[], rejectedFiles?: RejectedFile[]) => {
            addFiles(files, rejectedFiles);

            const doUpload = (file: File) => {
                uploadFile(file, (progress) => setLoadingProgress(file, progress))
                    .then(({ styleGuideId }) => setUploadSuccess(file, styleGuideId))
                    .catch((error: unknown) => {
                        if (error instanceof UploadAbortedError) {
                            return;
                        }

                        const errorMessage = error instanceof Error ? error.message : 'Upload failed';
                        setUploadError(file, errorMessage, () => doUpload(file));
                    });
            };

            files.forEach(doUpload);
        },
        [addFiles, setLoadingProgress, setUploadError, setUploadSuccess, uploadFile],
    );

    const completeUpload = useCallback(async () => {
        if (uploads == null) {
            return null;
        }

        const updates = uploads.filter(isFileUploadStaged).map(fileUploadToStyleGuideEdit);
        const response = await saveStyleGuides(updates);

        if ('error' in response) {
            showToast({ type: 'error', title: 'Error while saving changes. Please try again.' });

            return response;
        }

        reset();

        return response;
    }, [reset, saveStyleGuides, uploads]);

    useEffect(() => {
        if (filesToUpload !== null || filesRejectedForUpload !== null) {
            const addedFiles = filesToUpload?.filter((upload) => !hasBeenAdded(upload));
            const newRejectedFiles = filesRejectedForUpload?.filter((rejected) => !hasBeenAdded(rejected.file));

            acknowledgeStyleGuideFiles(addedFiles ?? [], newRejectedFiles);
            addFilesCallback(addedFiles ?? [], newRejectedFiles);
        }
    }, [acknowledgeStyleGuideFiles, addFiles, addFilesCallback, filesRejectedForUpload, filesToUpload, hasBeenAdded]);

    return {
        uploads,
        removeUpload: onRemoveCallback,
        resetStyleGuideUploads: reset,
        stageSuccessfulUploads: stageUpload,
        changeStyleGuideName: (upload: FileUpload, name: string) => updateStaged(upload, { name }),
        assignLanguage: (upload: FileUpload, language: Language | null) => {
            updateStaged(upload, { language: language ?? undefined });
        },
        completeUpload,
    };
};

export default useUploadStyleGuide;
